import React, { useState, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  COMPETENCIES,
  DIMENSIONS,
  TIER_MODELS,
  emptyMatrix,
  scoreOf,
  cohortRows,
  orgDimensionAverage,
} from "@/data/coachingData";
import { ScorecardInput } from "@/components/ScorecardInput";
import { Dashboard } from "@/components/Dashboard";
import { Recommendations } from "@/components/Recommendations";
import { ReportView } from "@/components/ReportView";
import { Landing } from "@/components/Landing";
import { LeadCaptureModal } from "@/components/LeadCaptureModal";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useBranding } from "@/hooks/useBranding";
import { useI18n } from "@/i18n";
import { usePrintGuard } from "@/hooks/usePrintGuard";
import { buildReportPdfBlob } from "@/lib/reportPdf";
import { buildCover } from "@/lib/reportCover";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  GraduationCap,
  Sparkles,
  Loader2,
  LayoutGrid,
  ClipboardList,
  FileText,
  Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachingApp() {
  const { t, lang, locale } = useI18n();
  const [started, setStarted] = useState(false);
  const [modelType, setModelType] = useState("4-tier");
  const [companyName, setCompanyName] = useState("");
  const [context, setContext] = useState("");
  const [scores, setScores] = useState(() => emptyMatrix("4-tier"));
  const [teamScores, setTeamScores] = useState({});
  const [teamEnabled, setTeamEnabled] = useState({});
  const [headcounts, setHeadcounts] = useState({});
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("scorecard");
  const [leadOpen, setLeadOpen] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  usePrintGuard();
  const { branding, logoUrl } = useBranding();
  const brandName = branding.brand_name || "CoachMeUpp";

  const coverData = () =>
    buildCover({
      t,
      locale,
      companyName: companyName || t("step.companyPlaceholder"),
      brandName,
      logoUrl,
      accent: branding.brand_color,
      bookingUrl: branding.booking_url,
      modelType,
      recommendations,
    });

  const setScore = (tierKey, compKey, value) => {
    setScores((prev) => ({ ...prev, [tierKey]: { ...prev[tierKey], [compKey]: value } }));
  };

  const setTeamScore = (tierKey, compKey, value) => {
    setTeamScores((prev) => ({ ...prev, [tierKey]: { ...(prev[tierKey] || {}), [compKey]: value } }));
  };

  const toggleTeam = (tierKey) => {
    setTeamEnabled((prev) => ({ ...prev, [tierKey]: !prev[tierKey] }));
    setTeamScores((prev) => {
      if (prev[tierKey]) return prev;
      const row = {};
      COMPETENCIES.forEach((c) => (row[c.key] = scoreOf(scores, tierKey, c.key)));
      return { ...prev, [tierKey]: row };
    });
  };

  const setHeadcount = (tierKey, value) => {
    setHeadcounts((prev) => ({ ...prev, [tierKey]: value }));
  };

  const handleModelChange = (val) => {
    setModelType(val);
    setScores(emptyMatrix(val));
    setTeamScores({});
    setTeamEnabled({});
    setHeadcounts({});
    setRecommendations(null);
  };

  const buildTiersPayload = () =>
    TIER_MODELS[modelType].map((tier) => ({
      tier_key: tier.key,
      tier_label: t(`tier.${tier.key}`),
      headcount: Number(headcounts[tier.key]) || 1,
      scores: COMPETENCIES.reduce((acc, c) => {
        acc[t(`comp.${c.key}`)] = scoreOf(scores, tier.key, c.key);
        return acc;
      }, {}),
      team_scores: teamEnabled[tier.key]
        ? COMPETENCIES.reduce((acc, c) => {
            acc[t(`comp.${c.key}`)] = scoreOf(teamScores, tier.key, c.key);
            return acc;
          }, {})
        : null,
    }));

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/recommendations/generate`, {
        company_name: companyName || t("step.companyPlaceholder"),
        model_type: modelType,
        context,
        lang,
        competencies: COMPETENCIES.map((c) => t(`comp.${c.key}`)),
        dimensions: Object.fromEntries(
          DIMENSIONS.map((d) => [d, +orgDimensionAverage(scores, TIER_MODELS[modelType], d).toFixed(1)])
        ),
        tiers: buildTiersPayload(),
      });
      setRecommendations(data);
      toast.success(t("toast.generated"));
      setTab("recommendations");
    } catch (e) {
      console.error(e);
      toast.error(t("toast.generateFailed"));
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (form) => {
    setSavingLead(true);
    try {
      const { data: lead } = await axios.post(`${API}/leads?defer_email=true`, {
        ...form,
        model_type: modelType,
        input_mode: "guided",
        lang,
        context,
        competencies: COMPETENCIES.map((c) => t(`comp.${c.key}`)),
        scores,
        headcounts: Object.fromEntries(
          TIER_MODELS[modelType].map((x) => [x.key, Number(headcounts[x.key]) || 1])
        ),
        team_scores: teamScores,
        cohort: cohortRows(scores, TIER_MODELS[modelType], headcounts).map((r) => ({
          ...r,
          tier_label: t(`tier.${r.tier_key}`),
          band_label: t(`band.${r.band}`),
        })),
        recommendations,
      });
      setLeadOpen(false);
      toast.success(t("toast.saved"));
      setTab("report");
      setTimeout(() => deliverPdf(lead.id), 700);
    } catch (e) {
      toast.error(e?.response?.status === 429 ? t("toast.rateLimit") : t("toast.error"));
    } finally {
      setSavingLead(false);
    }
  };

  // Build the branded PDF in the background and hand it to the backend so the
  // report email goes out with it attached (no local download).
  const deliverPdf = async (leadId) => {
    setPdfLoading(true);
    const id = toast.loading(t("app.preparingReport"));
    try {
      const blob = await buildReportPdfBlob({
        companyName: companyName || "Organization",
        brandName,
        cover: coverData(),
      });
      const fd = new FormData();
      fd.append("file", blob, "report.pdf");
      await axios.post(`${API}/leads/${leadId}/report-pdf`, fd);
      toast.success(t("app.reportEmailed"), { id });
    } catch (e) {
      console.error(e);
      toast.error(t("app.reportEmailFailed"), { id });
      try {
        await axios.post(`${API}/leads/${leadId}/send-report`);
      } catch (err) {
        console.error(err);
      }
    } finally {
      setPdfLoading(false);
    }
  };

  const tabsMeta = useMemo(
    () => [
      { v: "scorecard", label: t("tab.scorecard"), icon: ClipboardList },
      { v: "dashboard", label: t("tab.dashboard"), icon: LayoutGrid },
      { v: "recommendations", label: t("tab.recommendations"), icon: Sparkles },
      { v: "report", label: t("tab.report"), icon: FileText },
    ],
    [t]
  );

  if (!started) {
    return <Landing onStart={() => setStarted(true)} branding={branding} logoUrl={logoUrl} />;
  }

  return (
    <div className="min-h-screen bg-[#DCE4EF] text-slate-900">
      <header className="no-print sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-3 flex-wrap">
          <button
            className="flex items-center gap-3 text-left min-w-0"
            onClick={() => setStarted(false)}
            data-testid="brand-home-btn"
          >
            {logoUrl ? (
              <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5">
                <img src={logoUrl} alt={branding.brand_name} className="h-8 max-w-[150px] object-contain" data-testid="brand-logo" />
              </span>
            ) : (
              <>
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-xl shadow-lg"
                  style={{ background: branding.brand_color || "#6366F1" }}
                >
                  <GraduationCap size={22} className="text-white" />
                </span>
                <div>
                  <h1 className="font-heading text-lg font-bold leading-none tracking-tight">
                    {branding.brand_name || "CoachScore"}
                  </h1>
                  <p className="text-[11px] text-slate-500 leading-none mt-1">{t("brand.tagline")}</p>
                </div>
              </>
            )}
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageToggle light />
            <Button
              onClick={generate}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-500 text-white h-9"
              data-testid="generate-recommendations-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin sm:mr-1.5" />
                  <span className="hidden sm:inline">{t("app.generating")}</span>
                </>
              ) : (
                <>
                  <Sparkles size={16} className="sm:mr-1.5" />
                  <span className="hidden sm:inline">{t("app.generate")}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="no-print flex items-center justify-between gap-4 flex-wrap mb-5">
            <TabsList className="bg-white border border-slate-200 p-1 h-auto">
              {tabsMeta.map((x) => (
                <TabsTrigger
                  key={x.v}
                  value={x.v}
                  data-testid={`tab-${x.v}`}
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-600 gap-1.5 px-3 sm:px-4 py-1.5"
                >
                  <x.icon size={15} />
                  <span className="hidden sm:inline">{x.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => setLeadOpen(true)}
                className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="save-download-btn"
              >
                <Download size={15} className="sm:mr-1.5" />
                <span className="hidden sm:inline">{t("app.saveDownload")}</span>
              </Button>
            </div>
          </div>

          <TabsContent value="scorecard">
            <ScorecardInput
              modelType={modelType}
              teamScores={teamScores}
              setTeamScore={setTeamScore}
              teamEnabled={teamEnabled}
              toggleTeam={toggleTeam}
              onModelChange={handleModelChange}
              companyName={companyName}
              setCompanyName={setCompanyName}
              context={context}
              setContext={setContext}
              scores={scores}
              setScore={setScore}
              headcounts={headcounts}
              setHeadcount={setHeadcount}
              onFinish={generate}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="dashboard">
            <Dashboard
              modelType={modelType}
              scores={scores}
              teamScores={teamScores}
              headcounts={headcounts}
              recommendations={recommendations}
            />
          </TabsContent>

          <TabsContent value="recommendations">
            {recommendations ? (
              <Recommendations recommendations={recommendations} />
            ) : (
              <EmptyState onGenerate={generate} loading={loading} />
            )}
          </TabsContent>

          <TabsContent value="report">
            <ReportView
              companyName={companyName || t("step.companyPlaceholder")}
              modelType={modelType}
              context={context}
              scores={scores}
              teamScores={teamScores}
              headcounts={headcounts}
              recommendations={recommendations}
              brandName={branding.brand_name}
              logoUrl={logoUrl}
              bookingUrl={branding.booking_url}
            />
          </TabsContent>
        </Tabs>
      </main>

      <LeadCaptureModal
        open={leadOpen}
        onOpenChange={setLeadOpen}
        onSubmit={submitLead}
        submitting={savingLead}
      />
    </div>
  );
}

function EmptyState({ onGenerate, loading }) {
  const { t } = useI18n();
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-500/10 mb-4">
        <Sparkles size={28} className="text-indigo-600" />
      </span>
      <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{t("empty.title")}</h3>
      <p className="text-sm text-slate-500 max-w-md mb-6">{t("empty.desc")}</p>
      <Button
        onClick={onGenerate}
        disabled={loading}
        className="bg-indigo-600 hover:bg-indigo-500 text-white"
        data-testid="empty-generate-btn"
      >
        {loading ? (
          <Loader2 size={16} className="animate-spin mr-1.5" />
        ) : (
          <Sparkles size={16} className="mr-1.5" />
        )}
        {t("empty.cta")}
      </Button>
    </motion.div>
  );
}
